<template>
  <div class="pt-6">
    <div class="bg-white dark:bg-gray-800 relative shadow-sm sm:rounded-lg border border-gray-200">
      <!-- eslint-disable vue/no-unused-vars, vue/no-template-shadow -->
      <vue-slim-tables
        ref="table"
        :source="tableSource"
        :columns="columns"
        class="table"
        :per-page="pageSize">
        <template #thead="{ columns }">
          <tr>
            <th
              v-for="(column) in columns"
              :key="column.key"
              :class="column.class">
              {{ column.title }}
            </th>
          </tr>
        </template>

        <template #row="{ row }">
          <tr>
            <td class="w-32">
              <div class="inline-flex truncate">
                <a :href="`/incoming_calls/${row.id}`"
                  class="w-48 cursor-pointer font-medium text-primary-600 dark:text-primary-500 hover:underline truncate">
                  {{ row.display_name }}
                </a>
              </div>
            </td>

            <td class="w-32">
              {{ row.received }}
            </td>

            <td class="w-48">
              <div class="inline-flex truncate">
                <span class="truncate">
                  {{ row.from }}
                </span>
              </div>
            </td>

            <td class="w-48">
              {{ row.phone_number }}
            </td>

            <td>
              {{ row.from_address }}
            </td>

            <td class="recording">
              <div v-if="!row.recording_url" class="p-2 pl-8">
                No recording
              </div>
              <audio controls v-else>
                <source :src="row.recording_url" type="audio/mpeg">
              </audio>
            </td>

            <td class="text-right">
              <div class="flex w-32 text-left justify-start">
                <fwb-tooltip
                  theme="light">
                  <template #trigger>
                    <span v-if="callSucceeded(row)"
                      class="w-24 inline-block text-left bg-green-100 text-green-800 text-xs font-medium my-0 pl-1 pr-1.5 py-0.5 rounded dark:bg-green-900 dark:text-green-300">
                      <svg class="w-4 h-4 text-green-400 inline" aria-hidden="true" xmlns="http://www.w3.org/2000/svg"
                        width="24" height="24" fill="none" viewBox="0 0 24 24">
                        <path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                          d="M5 11.917 9.724 16.5 19 7.5" />
                      </svg>

                      {{ humanize(row.call_status) }}
                    </span>

                    <span v-else
                      class="w-24 inline-block text-left bg-red-100 text-red-800 text-xs font-medium my-0 pl-1 pr-1.5 py-0.5 rounded dark:bg-red-900 dark:text-red-300">
                      <svg class="w-4 h-4 text-red-400 inline" xmlns="http://www.w3.org/2000/svg" width="24" height="24"
                        fill="none" viewBox="0 0 24 24">
                        <path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                          d="M7.757 12h8.486M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" />
                      </svg>

                      {{ humanize(row.call_status || 'Failed') }}
                    </span>
                  </template>
                  <template #content>
                    <span class="text-sm text-gray-500">
                      {{ row.call_status_details }}
                    </span>
                  </template>
                </fwb-tooltip>

                <fwb-tooltip
                  v-if="isVoicemail(row)"
                  theme="light">
                  <template #trigger>
                    <span
                      class="inline-block text-left bg-primary-100 text-xs font-medium ml-1 mr-1 pl-1 pr-1.5 py-0.5 rounded dark:bg-indigo-900 dark:text-indigo-300">
                      <svg class="w-4 h-4 text-primary-500  inline" aria-hidden="true" xmlns="http://www.w3.org/2000/svg"
                        width="24" height="24" fill="none" viewBox="0 0 24 24">
                        <path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                          d="M11 16v-5.5A3.5 3.5 0 0 0 7.5 7m3.5 9H4v-5.5A3.5 3.5 0 0 1 7.5 7m3.5 9v4M7.5 7H14m0 0V4h2.5M14 7v3m-3.5 6H20v-6a3 3 0 0 0-3-3m-2 9v4m-8-6.5h1" />
                      </svg>
                    </span>
                  </template>
                  <template #content>
                    <span class="text-sm text-gray-500">
                      Voicemail received
                    </span>
                  </template>
                </fwb-tooltip>
              </div>
            </td>
          </tr>
        </template>

        <template #pagination>
          <nav class="flex flex-col md:flex-row justify-between items-start md:items-center space-y-3 md:space-y-0">
            <span class="text-sm font-normal text-gray-500 dark:text-gray-400">
              Showing
              <span class="font-semibold text-gray-900 dark:text-white">{{ currentPageInfo() }}</span>
              of
              <span class="font-semibold text-gray-900 dark:text-white">{{ totalCount }}</span>
            </span>

            <VuejsPaginate
              v-if="totalPagesCount > 1"
              v-model="currentPage"
              :page-count="totalPagesCount"
              :prev-text="'<'"
              :next-text="'>'" />
          </nav>
        </template>
      </vue-slim-tables>
      <!-- eslint-enable vue/no-unused-vars, vue/no-template-shadow -->
    </div>
  </div>
</template>

<script setup>
import { watch, ref } from 'vue'
import qs from 'qs'
import VuejsPaginate from 'vuejs-paginate-next'
import VueSlimTables from 'vue-slim-tables'
import { FwbTooltip } from 'flowbite-vue'
import 'vue-slim-tables/stylesheets'
import securedAxios from '../../utils/axios'
import { toastError } from '~/javascript/utils/toast'
import { humanize } from '~/javascript/utils/strings'

const props = defineProps({
  filters: {
    type: Object,
    required: false,
    default: () => {}
  }
})

let searchTriggerTimeout = null

const axios = securedAxios()

const table = ref()
const totalPagesCount = ref(200)
const totalCount = ref(0)
const currentPage = ref(1)
const pageSize = ref(20)

const columns = [
  { title: 'Name', key: 'display_name', class: 'text-left' },
  { title: 'Received', key: 'received', class: 'text-left' },
  { title: 'From', key: 'from', class: 'text-left' },
  { title: 'To', key: 'phone_number', class: 'text-left' },
  { title: 'State/City/Zip', key: 'from_address', class: 'text-left' },
  { title: 'Recording/Voicemail', key: 'record', class: 'text-left recording' },
  { title: 'Status', key: 'call_status', class: 'text-left' }
]

async function tableSource(params) {
  let response = null

  const { filters: { dates, search, callStatuses } } = props
  try {
    const queryParams = {
      ...params,
      query: search,
      page: currentPage.value,
      call_statuses: callStatuses
    }

    if (dates) {
      const [fromDate, toDate] = dates
      queryParams.from_date = fromDate
      queryParams.to_date = toDate
    }

    response = await axios.get(
      '/ajax/incoming_calls',
      {
        params: queryParams,
        paramsSerializer(json) {
          return qs.stringify(json, { arrayFormat: 'brackets' })
        }
      }
    )
  } catch (error) {
    toastError(error)
  }

  totalPagesCount.value = parseInt(response.headers['total-pages'], 10)
  totalCount.value = parseInt(response.headers['total-count'], 10)
  currentPage.value = parseInt(response.headers['page-number'], 10)
  pageSize.value = parseInt(response.headers['page-size'], 10)

  return response?.data || []
}

function currentPageInfo() {
  let firstVisible = pageSize.value * (currentPage.value - 1) + 1
  const lastVisible = Math.min(firstVisible + pageSize.value - 1, totalCount.value)

  if (firstVisible > lastVisible) {
    firstVisible = lastVisible
  }

  return `${firstVisible}-${lastVisible}`
}

function callSucceeded(row) {
  return row.call_status === 'answered' || row.call_status === 'voicemail'
}

function isVoicemail(row) {
  return row.recording_type === 'voicemail'
}

watch(currentPage, (_val) => {
  table?.value?.reload()
})

watch(() => props.filters, (_val) => {
  clearTimeout(searchTriggerTimeout)
  searchTriggerTimeout = setTimeout(() => {
    // Reset the page number to 1 when searching otherwise the table will crash
    currentPage.value = 1
    table?.value?.reload()
  }, 500)
}, {
  deep: true
})
</script>

<style scoped>
.vst.table tbody tr td.recording {
  padding: 3px 0px 3px 0px;
}

audio {
  height: 38px;
  scale: 85%;
  margin-left: -8px;
}
</style>
